import { useState, useCallback } from 'react';
import type { VerticalLine } from '@/modules/shared/DataLayers/types';

export interface UseHarmonicManagementProps {
  verticalLines: VerticalLine[];
}

export interface UseHarmonicManagementReturn {
  // Harmonic selections state (per chart)
  harmonicSelectionsByChart: Record<string, any[]>;
  setHarmonicSelectionsByChart: React.Dispatch<React.SetStateAction<Record<string, any[]>>>;
  // Focused harmonic group per chart
  focusedHarmonicByChart: Record<string, string | null>;
  setFocusedHarmonicByChart: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;
  // Utility functions
  getHarmonicGroupCount: (sectionKey: string) => number;
  getAvailableHarmonicPrefixes: (sectionKey: string) => string[];
}

/**
 * Manages harmonic-related state and utilities
 * Handles harmonic selections per chart and focused harmonic groups
 * Group count is calculated dynamically via getActualGroupCount() instead of stored in state
 */
export function useHarmonicManagement({ verticalLines }: UseHarmonicManagementProps): UseHarmonicManagementReturn {
  // Harmonic selection state per chart
  const [harmonicSelectionsByChart, setHarmonicSelectionsByChart] = useState<Record<string, any[]>>({});
  // Group count is now calculated dynamically via getActualGroupCount() instead of stored in state
  // Track focused harmonic group per chart (chartId -> group ID)
  const [focusedHarmonicByChart, setFocusedHarmonicByChart] = useState<Record<string, string | null>>({});

  // Helper function to count HARMONIC groups only (H, I, J prefixes)
  const getHarmonicGroupCount = useCallback(
    (sectionKey: string): number => {
      const harmonicLinesInSection = verticalLines.filter((line: VerticalLine) => {
        if (!line.isHarmonic) return false;
        if (!(line.sectionKey === sectionKey || line.chartId === sectionKey)) return false;

        // Only count H, I, J prefixes
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (!match) return false;
        const prefix = match[1];
        return ['H', 'I', 'J'].includes(prefix);
      });

      // Get unique label prefixes (H, I, J)
      const uniquePrefixes = new Set<string>();
      harmonicLinesInSection.forEach((line: VerticalLine) => {
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (match) uniquePrefixes.add(match[1]);
      });

      return uniquePrefixes.size;
    },
    [verticalLines]
  );

  // Helper function to get available harmonic prefixes for a section
  // Returns array of unused prefixes from [H, I, J]
  // Considers both current prefixes AND originalPrefixes (which are "taken" by renamed groups)
  const getAvailableHarmonicPrefixes = useCallback(
    (sectionKey: string): string[] => {
      const allPrefixes = ['H', 'I', 'J'];
      const harmonicLinesInSection = verticalLines.filter((line: VerticalLine) => {
        if (!line.isHarmonic) return false;
        if (!(line.sectionKey === sectionKey || line.chartId === sectionKey)) return false;

        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (!match) return false;
        const prefix = match[1];
        return ['H', 'I', 'J'].includes(prefix) || ['H', 'I', 'J'].includes(line.originalPrefix || '');
      });

      // Get used prefixes (both current AND original)
      const usedPrefixes = new Set<string>();
      harmonicLinesInSection.forEach((line: VerticalLine) => {
        // Add current prefix
        const match = line.label?.match(/^-?([A-Z])\d+/);
        if (match && ['H', 'I', 'J'].includes(match[1])) {
          usedPrefixes.add(match[1]);
        }
        // Add original prefix if exists (this means it was renamed, so original is "taken")
        if (line.originalPrefix && ['H', 'I', 'J'].includes(line.originalPrefix)) {
          usedPrefixes.add(line.originalPrefix);
        }
      });

      // Return available (unused) prefixes
      return allPrefixes.filter((p) => !usedPrefixes.has(p));
    },
    [verticalLines]
  );

  return {
    harmonicSelectionsByChart,
    setHarmonicSelectionsByChart,
    focusedHarmonicByChart,
    setFocusedHarmonicByChart,
    getHarmonicGroupCount,
    getAvailableHarmonicPrefixes,
  };
}
